// COLORS: #0D47A1, #2196F3, #90CAF9, #E3F2FD ONLY (+ #FFFFFF as neutral surface/text-on-dark)
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName } from "./schoolNameStore";
import { School, User, KeyRound, AlertTriangle, EyeOff } from "lucide-react";
const Login = () => {
  const schoolName = useSchoolName();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    if (!username || !password) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('https://schoolcrm-fzcv.onrender.com/api/auth/parent-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password
        })
      });

      // Check if response is OK
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      const data = await response.json();

      // Store parent login info in localStorage
      localStorage.setItem('parentLogin', JSON.stringify({
        parentId: data.parentId,
        studentId: data.studentId,
        studentName: data.studentName,
        parentName: data.parentName,
        className: data.className,
        username: username
      }));

      // Redirect to parent dashboard
      navigate('/parent-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return <div style={styles.page}>
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      <div style={styles.card}>
        <div style={styles.logoCircle}><School className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>

        <h1 style={styles.title}>{schoolName}</h1>
        <p style={styles.subtitle}>Parent Portal</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>USERNAME</label>
            <div style={styles.inputWrapper}>
              <span style={styles.icon}><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              <input type="text" placeholder="Enter your username" value={username} onChange={e => setUsername(e.target.value)} style={styles.input} />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>PASSWORD</label>
            <div style={styles.inputWrapper}>
              <span style={styles.icon}><KeyRound className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              <input type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} style={styles.input} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
                {showPassword ? <EyeOff className="inline w-[1em] h-[1em] align-[-0.125em]" /> : ""}
              </button>
            </div>
          </div>

          {error && <div style={styles.errorBox}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {error}</div>}

          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? "⏳ Signing In..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>;
};
const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0D47A1 0%, #2196F3 50%, #0D47A1 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Segoe UI', sans-serif",
    position: "relative",
    overflow: "hidden",
    padding: "20px"
  },
  blob1: {
    position: "absolute",
    top: "-80px",
    right: "-80px",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(144,202,249,0.35) 0%, transparent 70%)",
    pointerEvents: "none"
  },
  blob2: {
    position: "absolute",
    bottom: "-100px",
    left: "-100px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(227,242,253,0.25) 0%, transparent 70%)",
    pointerEvents: "none"
  },
  card: {
    background: "#FFFFFF",
    backdropFilter: "blur(20px)",
    borderRadius: "20px",
    padding: "40px 36px",
    width: "100%",
    maxWidth: "400px",
    border: "1px solid #90CAF9",
    boxShadow: "0 8px 48px rgba(13,71,161,0.35), 0 0 0 1px rgba(144,202,249,0.15)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  logoCircle: {
    width: "68px",
    height: "68px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #0D47A1, #2196F3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    marginBottom: "12px",
    boxShadow: "0 4px 20px rgba(13,71,161,0.4)"
  },
  title: {
    color: "#0D47A1",
    fontSize: "24px",
    fontWeight: "600",
    margin: "0 0 4px"
  },
  subtitle: {
    color: "#2196F3",
    fontSize: "12px",
    margin: "0 0 28px",
    letterSpacing: "0.5px"
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  label: {
    color: "#0D47A1",
    fontSize: "11px",
    fontWeight: "600",
    letterSpacing: "1.5px"
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center"
  },
  icon: {
    position: "absolute",
    left: "14px",
    fontSize: "14px",
    pointerEvents: "none"
  },
  input: {
    width: "100%",
    padding: "13px 14px 13px 42px",
    background: "#E3F2FD",
    border: "1px solid #90CAF9",
    borderRadius: "10px",
    color: "#0D47A1",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box"
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    padding: "4px"
  },
  errorBox: {
    background: "#E3F2FD",
    border: "1px solid #90CAF9",
    borderRadius: "8px",
    padding: "8px 12px",
    color: "#0D47A1",
    fontSize: "13px"
  },
  submitBtn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #0D47A1, #2196F3)",
    border: "none",
    borderRadius: "10px",
    color: "#FFFFFF",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(13,71,161,0.4)",
    marginTop: "4px"
  }
};
export default Login;